import { supabase } from "@/api/supabaseClient";
import { allowNextSignedIn, cancelExplicitSignIn } from "@/auth/webSignOutLatch";
import { InvitationsApi, type InvitePreview } from "@/api/invitations/InvitationsApi";
import { useAuth } from "@/auth/AuthContext";
import { queryParam } from "@/auth/linking";
import { routes } from "@/constants/routes";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { ErrorText } from "@/ui/components/ErrorText";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { LinkText } from "@/ui/components/LinkText";
import { uiErrorMessage } from "@/utils/isoDate";
import * as Linking from "expo-linking";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { ActivityIndicator, ScrollView, Text } from "react-native";

const MIN_PASSWORD = 8;

export default function AcceptInviteScreen() {
  const { theme } = useTheme();
  const router = useRouter();
  const { session } = useAuth();
  const params = useLocalSearchParams<{ token?: string }>();
  const [token, setToken] = useState<string | null>(
    typeof params.token === "string" ? params.token : null,
  );
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (token) return;
    let active = true;
    Linking.getInitialURL().then((url) => {
      const fromUrl = queryParam(url, "token");
      if (active && fromUrl) setToken(fromUrl);
    });
    const sub = Linking.addEventListener("url", ({ url }) => {
      const fromUrl = queryParam(url, "token");
      if (fromUrl) setToken(fromUrl);
    });
    return () => {
      active = false;
      sub.remove();
    };
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let active = true;
    setLoading(true);
    InvitationsApi.preview(token)
      .then((row) => {
        if (!active) return;
        setPreview(row);
        setName(row.displayName);
      })
      .catch((e: unknown) => {
        if (!active) return;
        setPreviewError(uiErrorMessage(e, "Einladung ungültig."));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  const sessionEmail = session?.user.email?.toLowerCase() ?? "";
  const sameAccount = Boolean(preview?.accountExists && sessionEmail && sessionEmail === preview.email.toLowerCase());
  const closed = preview && preview.status !== "open";

  const submit = async () => {
    if (!token || !preview || closed) return;
    setError(null);
    if (name.trim().length < 1) {
      setError("Name ist erforderlich.");
      return;
    }
    setLoading(true);
    try {
      if (preview.accountExists && !sameAccount) {
        allowNextSignedIn();
        const { error: signError } = await supabase.auth.signInWithPassword({
          email: preview.email,
          password,
        });
        if (signError) throw signError;
      } else if (!preview.accountExists) {
        if (password !== confirm) throw new Error("Passwörter stimmen nicht überein.");
        if (password.length < MIN_PASSWORD) {
          throw new Error(`Passwort mindestens ${MIN_PASSWORD} Zeichen.`);
        }
      }
      const result = await InvitationsApi.accept({
        token,
        displayName: name,
        password: preview.accountExists ? undefined : password,
      });
      if (result.session) {
        allowNextSignedIn();
        let sawTokenRefreshed = false;
        const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
          if (event === "TOKEN_REFRESHED") sawTokenRefreshed = true;
        });
        try {
          const { error: sessionError } = await supabase.auth.setSession(result.session);
          if (sessionError) throw sessionError;
          // setSession kann TOKEN_REFRESHED statt SIGNED_IN liefern und danach
          // noch INITIAL_SESSION. Das Flag gilt nur für SIGNED_IN.
          if (sawTokenRefreshed) cancelExplicitSignIn();
        } finally {
          authListener.subscription.unsubscribe();
        }
      }
      router.replace(routes.home);
    } catch (e: unknown) {
      cancelExplicitSignIn();
      setError(uiErrorMessage(e, "Einladung konnte nicht angenommen werden."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView
      contentContainerStyle={{
        flexGrow: 1,
        justifyContent: "center",
        padding: 24,
        gap: 16,
        maxWidth: 440,
        alignSelf: "center",
        width: "100%",
      }}
    >
      <Text style={{ color: theme.color.text, fontSize: theme.fontSize.title, fontWeight: "700" }}>
        Einladung annehmen
      </Text>
      {!token ? (
        <Text style={{ color: theme.color.danger }}>Der Einladungslink ist unvollständig.</Text>
      ) : null}
      {loading && !preview ? <ActivityIndicator color={theme.color.primary} /> : null}
      {previewError ? <ErrorText>{previewError}</ErrorText> : null}
      {preview ? (
        <>
          <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.body }}>
            {preview.organizationName
              ? `Du wirst Mitglied von „${preview.organizationName}“.`
              : "Du wirst Mitglied der Organisation."}{" "}
            Die Rollen sind festgelegt: {preview.rolesLabel || "—"}.
          </Text>
          <Text style={{ color: theme.color.text }}>E-Mail: {preview.email}</Text>
          {closed ? (
            <Text style={{ color: theme.color.danger }}>
              Diese Einladung ist {preview.status === "expired" ? "abgelaufen" : "nicht mehr offen"}.
            </Text>
          ) : (
            <>
              <FloatingLabelInput label="Name" value={name} onChangeText={setName} autoCapitalize="words" />
              {preview.accountExists && !sameAccount ? (
                <FloatingLabelInput
                  label="Bestehendes Passwort"
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry
                />
              ) : null}
              {!preview.accountExists ? (
                <>
                  <FloatingLabelInput
                    label="Passwort"
                    value={password}
                    onChangeText={setPassword}
                    secureTextEntry
                  />
                  <FloatingLabelInput
                    label="Passwort bestätigen"
                    value={confirm}
                    onChangeText={setConfirm}
                    secureTextEntry
                  />
                </>
              ) : null}
              {sameAccount ? (
                <Text style={{ color: theme.color.muted }}>
                  Du bist bereits mit diesem Konto angemeldet.
                </Text>
              ) : null}
              {error ? <ErrorText>{error}</ErrorText> : null}
              <AppButton onPress={submit} disabled={loading} loading={loading}>
                {loading ? "Wird angenommen…" : "Einladung annehmen"}
              </AppButton>
            </>
          )}
        </>
      ) : null}
      <LinkText onPress={() => router.replace(routes.login)} color={theme.color.muted}>
        Zum Login
      </LinkText>
    </ScrollView>
  );
}
